import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { formStyles } from './shared-styles.js';
import { AuthService, authService as defaultAuthService } from '../services/auth-service.js';
import { isApiError } from '../services/api-client.js';

/**
 * Account registration form. Sign-up requires email confirmation, so on success it does NOT establish
 * a session — instead it shows a "check your email" confirmation. Validation/registration errors are
 * rendered inline.
 */
@customElement('gc-register-form')
export class GcRegisterForm extends LitElement {
  static override styles = [
    formStyles,
    css`
      .dev-confirm {
        margin-top: 0.85rem; padding: 0.55rem 0.7rem; border: 1px dashed var(--gc-border, #d8e0d8);
        border-radius: 0.5rem; background: var(--gc-surface-soft, #f1f5f1); color: var(--gc-ink, #16241c);
        font-size: 0.9rem;
      }
    `,
  ];

  /** Injectable for testing; defaults to the shared AuthService. */
  authService: AuthService = defaultAuthService;

  @property({ type: String })
  email = '';

  @state()
  private loading = false;

  @state()
  private error = '';

  /** Set once registration succeeds; the form is replaced by a confirmation prompt. */
  @state()
  private registeredEmail: string | null = null;

  /** In Development, a click-to-confirm link the server returned (null in production). */
  @state()
  private devConfirmUrl: string | null = null;

  override render() {
    if (this.registeredEmail !== null) {
      return html`<div class="success" role="status">
        <p>Account created! We've sent a confirmation link to <strong>${this.registeredEmail}</strong>.</p>
        <p>Please confirm your email address, then <a href="/login">sign in</a>.</p>
        ${this.devConfirmUrl
          ? html`<p class="dev-confirm">Development environment: <a href=${this.devConfirmUrl}>click here to confirm</a>.</p>`
          : nothing}
      </div>`;
    }
    return html`
      <form @submit=${this.onSubmit}>
        <label>
          Display name
          <input type="text" name="displayName" autocomplete="nickname" />
        </label>
        <label>
          Email
          <input
            type="email"
            name="email"
            autocomplete="username"
            required
            .value=${this.email}
            @input=${(e: Event) => (this.email = (e.target as HTMLInputElement).value)}
          />
        </label>
        <label>
          Password
          <input type="password" name="password" autocomplete="new-password" required minlength="8" />
        </label>
        <div class="error" role="alert">${this.error}</div>
        <button type="submit" ?disabled=${this.loading}>
          ${this.loading ? 'Creating account…' : 'Create account'}
        </button>
      </form>
    `;
  }

  private async onSubmit(event: Event) {
    event.preventDefault();
    if (this.loading) {
      return;
    }
    const form = event.target as HTMLFormElement;
    const data = new FormData(form);
    const email = String(data.get('email') ?? '');
    const password = String(data.get('password') ?? '');
    const displayNameRaw = String(data.get('displayName') ?? '').trim();
    const displayName = displayNameRaw.length > 0 ? displayNameRaw : undefined;

    this.error = '';
    this.loading = true;
    try {
      const result = await this.authService.register(email, password, displayName);
      this.registeredEmail = result.email ?? email;
      this.devConfirmUrl = result.devConfirmUrl ?? null;
    } catch (err) {
      this.error = isApiError(err) ? err.message : 'Registration failed. Please try again.';
    } finally {
      this.loading = false;
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gc-register-form': GcRegisterForm;
  }
}
